import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import dayjs from 'dayjs';
import {
  Button,
  DatePicker,
  FieldError,
  FieldHelper,
  SearchableDropdown,
  SideBarModal,
  Skeleton,
  TextareaField,
} from '@/components';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { Maintenance } from '@/types/common';
import {
  ESCALATION_STATUS_LABEL,
  PauseBillingResult,
  RentalDetail,
} from '@/types/rental';
import {
  createMaintenance,
  CreateMaintenanceRequest,
  getListMaintenances,
} from '@/services/api/maintenances';
import { pauseRentalBilling } from '@/services/api/rentals';
import { extractApiErrorMessage, isConflictError } from '@/utils/errorHandler';
import { formatRupiah } from '@/utils/format';
import { getUserEmail } from '@/utils/storage';
import CreateMaintenanceModal from '@/pages/maintenance/components/CreateMaintenanceModal';
import {
  BillingPauseForm,
  billingPauseBlocker,
  billingPausePreviewKey,
  BillingPausePreviewState,
  firstChargeDateOf,
  formatTimelineDate,
  formatVehicleLabel,
  MAINTENANCE_NOT_REPAIRING,
  repossessionLinkLabel,
} from '../rentalDisplay';

interface BillingPauseModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * The pause landed, or the rental's state moved under the form (409): the
   * parent closes the modal and re-reads the detail either way.
   */
  onDone: () => void;
  detail: RentalDetail;
}

/** How far back an OTHER pause may start (fleet's OTHER_MAX_BACKDATE_DAYS). */
const OTHER_MAX_BACKDATE_DAYS = 7;
const PREVIEW_DEBOUNCE_MS = 300;

const isoDate = (value: string) => dayjs(value).format('YYYY-MM-DD');

const blankForm = (): BillingPauseForm => ({
  reason: 'VEHICLE_REPAIR',
  maintenanceID: '',
  startDate: dayjs().format('YYYY-MM-DD'),
  notes: '',
});

/** The request for this form; `notes` only when there are some. */
const pauseBody = (form: BillingPauseForm, notes: string) => ({
  reason: form.reason,
  ...(form.reason === 'VEHICLE_REPAIR'
    ? { maintenanceID: form.maintenanceID }
    : {}),
  startDate: form.startDate,
  ...(notes ? { notes } : {}),
});

const ImpactRow: React.FC<{
  label: string;
  value: string;
  strong?: boolean;
}> = ({ label, value, strong }) => (
  <div className="flex justify-between gap-3 py-1">
    <span
      className={`text-[13px] ${strong ? 'font-medium text-gray-900' : 'text-gray-600'}`}
    >
      {label}
    </span>
    <span
      className={`text-right text-[14px] tabular-nums text-gray-900 ${strong ? 'font-medium' : ''}`}
    >
      {value}
    </span>
  </div>
);

/**
 * Jeda Tagihan: stops a rental's daily billing while its unit is in the
 * workshop. The impact panel is the endpoint's own dry run, and the submit
 * stays disabled until the dry run of the form AS IT IS NOW has succeeded.
 */
const BillingPauseModal: React.FC<BillingPauseModalProps> = ({
  isOpen,
  onClose,
  onDone,
  detail,
}) => {
  const today = dayjs().format('YYYY-MM-DD');
  const [form, setForm] = useState<BillingPauseForm>(blankForm);
  const [maintenances, setMaintenances] = useState<Maintenance[]>([]);
  const [maintenanceState, setMaintenanceState] = useState<
    'loading' | 'ok' | 'error'
  >('loading');
  const [preview, setPreview] = useState<
    | (BillingPausePreviewState & {
        result?: PauseBillingResult;
        error?: string;
      })
    | null
  >(null);
  const [retryToken, setRetryToken] = useState(0);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCreateMaintenanceOpen, setIsCreateMaintenanceOpen] = useState(false);
  const reasonRef = useRef<HTMLInputElement>(null);

  const vehicleID = detail.vehicle?.id;
  const plate = detail.vehicle?.platNomor ?? '-';

  const loadMaintenances = useCallback(
    async (select?: string) => {
      if (!vehicleID) return;
      setMaintenanceState('loading');
      try {
        // ponytail: the list filters by search, not vehicle — the plate
        // narrows it and the vehicleID check makes it exact. Add a vehicleID
        // filter to GET /v1/maintenances if a unit ever has >100 matches.
        const response = await getListMaintenances({
          page: 1,
          size: 100,
          search: detail.vehicle?.platNomor,
        });
        const open = (response.data ?? []).filter(
          (m) =>
            m.vehicleID === vehicleID &&
            !MAINTENANCE_NOT_REPAIRING.includes(m.status)
        );
        setMaintenances(open);
        setMaintenanceState('ok');
        const pick = select ?? (open.length === 1 ? open[0].ID : undefined);
        if (pick) setForm((f) => ({ ...f, maintenanceID: pick }));
      } catch {
        setMaintenanceState('error');
      }
    },
    [vehicleID, detail.vehicle?.platNomor]
  );

  useEffect(() => {
    if (!isOpen) return;
    setForm(blankForm());
    setPreview(null);
    setSubmitError('');
    loadMaintenances();
  }, [isOpen, loadMaintenances]);

  const maintenanceOptions = useMemo(
    () =>
      maintenances.map((m) => ({
        value: m.ID,
        label: `${m.code} · ${
          m.categories?.join(', ') || m.issueDescription || '-'
        } · dibuka ${formatTimelineDate(m.maintenanceDate)}`,
      })),
    [maintenances]
  );

  const selectedMaintenance = maintenances.find(
    (m) => m.ID === form.maintenanceID
  );

  // The same floor the endpoint enforces, known before the first preview so
  // the picker never offers a day the dry run would reject: the maintenance's
  // open date (or 7 days back for OTHER), never before billing started, never
  // back into an earlier pause.
  const minStartDate = useMemo(() => {
    const floors = [
      form.reason === 'VEHICLE_REPAIR'
        ? selectedMaintenance && isoDate(selectedMaintenance.maintenanceDate)
        : dayjs(today)
            .subtract(OTHER_MAX_BACKDATE_DAYS, 'day')
            .format('YYYY-MM-DD'),
      detail.handoverDate && firstChargeDateOf(detail.handoverDate),
      ...(detail.billingPause?.history ?? []).map((p) => p.endDate),
    ].filter((d): d is string => Boolean(d));
    return floors.sort()[floors.length - 1] ?? today;
  }, [form.reason, selectedMaintenance, today, detail]);

  useEffect(() => {
    if (form.startDate < minStartDate) {
      setForm((f) => ({ ...f, startDate: minStartDate }));
    }
  }, [form.startDate, minStartDate]);

  // Debounced dry run. Only the answer for the latest key is kept: a slow
  // response for an older date must not overwrite the current one.
  const previewKey = billingPausePreviewKey(form);
  const needsMaintenance =
    form.reason === 'VEHICLE_REPAIR' && !form.maintenanceID;
  useEffect(() => {
    if (!isOpen || needsMaintenance) {
      setPreview(null);
      return undefined;
    }
    const key = previewKey;
    let stale = false;
    setPreview({ key, status: 'loading' });
    const timer = setTimeout(async () => {
      try {
        const result = await pauseRentalBilling(detail.id, {
          // OTHER needs notes to validate, but notes change no number; the
          // dry run writes nothing, so a stand-in keeps the panel live while
          // ops are still typing. The real notes go with the submit.
          ...pauseBody(
            form,
            form.reason === 'OTHER' ? form.notes.trim() || 'preview' : ''
          ),
          dryRun: true,
        });
        if (!stale) setPreview({ key, status: 'ok', result });
      } catch (error) {
        if (!stale) {
          setPreview({
            key,
            status: 'error',
            error: extractApiErrorMessage(error, 'Gagal memuat dampak.'),
          });
        }
      }
    }, PREVIEW_DEBOUNCE_MS);
    return () => {
      stale = true;
      clearTimeout(timer);
    };
    // Notes are deliberately absent: see billingPausePreviewKey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, previewKey, needsMaintenance, retryToken, detail.id]);

  const blocker = billingPauseBlocker(form, preview);
  const result = preview?.status === 'ok' ? preview.result : undefined;
  const repossession = detail.repossession
    ? repossessionLinkLabel(detail.repossession)
    : result?.repossession
      ? repossessionLinkLabel(result.repossession)
      : null;

  const handleSubmit = async () => {
    if (blocker) return;
    setIsSubmitting(true);
    setSubmitError('');
    try {
      await pauseRentalBilling(detail.id, pauseBody(form, form.notes.trim()));
      showSuccessToast(
        'Success',
        `Tagihan ${detail.code ?? ''} dijeda sejak ${formatTimelineDate(form.startDate)}`
      );
      onDone();
    } catch (error) {
      const message = extractApiErrorMessage(
        error,
        'Gagal menjeda tagihan. Coba lagi.'
      );
      // Someone else paused it, or the rental left the pausable state: the
      // form no longer describes the rental, so close and re-read it.
      if (isConflictError(error)) {
        showErrorToast('Error', message);
        onDone();
      } else {
        setSubmitError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateMaintenance = async (data: CreateMaintenanceRequest) => {
    const created = await createMaintenance({
      ...data,
      createdBy: getUserEmail(),
    });
    showSuccessToast('Success', 'Maintenance dibuat dan dipilih');
    await loadMaintenances(created?.data?.ID);
  };

  if (!isOpen) return null;

  return (
    <>
      <SideBarModal
        isOpen
        onClose={onClose}
        position="center"
        width="md"
        initialFocusRef={reasonRef}
        header={
          <h2 className="text-[18px] font-semibold text-gray-900">
            Jeda Tagihan Harian
          </h2>
        }
        footer={
          <div className="flex flex-col-reverse gap-2 md:flex-row md:items-center md:justify-end">
            {blocker && (
              <p
                id="billing-pause-blocker"
                className="text-center text-[12px] text-gray-600 md:mr-auto md:text-left"
              >
                {blocker}
              </p>
            )}
            <Button
              variant="secondary"
              size="medium"
              onClick={onClose}
              className="h-12 w-full md:h-auto md:w-auto"
            >
              Batal
            </Button>
            <Button
              variant="primary"
              size="medium"
              disabled={Boolean(blocker) || isSubmitting}
              ariaDescribedBy={blocker ? 'billing-pause-blocker' : undefined}
              onClick={handleSubmit}
              className="h-12 w-full md:h-auto md:w-auto"
            >
              {isSubmitting ? 'Memproses...' : 'Jeda Tagihan'}
            </Button>
          </div>
        }
      >
        <div className="space-y-4 px-4 py-4 md:px-6 md:py-5">
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-[13px] text-gray-700">
            <span className="font-medium">{detail.code ?? '-'}</span>
            {detail.driver?.name ? ` · ${detail.driver.name}` : ''} · {plate}
          </div>

          {repossession && (
            <div
              role="alert"
              className="flex gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-[13px] text-red-700"
            >
              <i
                className="ri-error-warning-line text-[16px]"
                aria-hidden="true"
              ></i>
              <p>
                Rental ini punya case penarikan aktif —{' '}
                <span className="font-medium">{repossession}</span>. Jeda tidak
                menutup case penarikan; info ini ikut di alert Slack.
              </p>
            </div>
          )}

          <fieldset>
            <legend className="text-[13px] text-gray-500 mb-1">
              Alasan <span className="text-red-500">*</span>
            </legend>
            <div className="flex flex-wrap gap-5">
              {(
                [
                  ['VEHICLE_REPAIR', 'Perbaikan unit'],
                  ['OTHER', 'Lainnya'],
                ] as const
              ).map(([value, label]) => (
                <label
                  key={value}
                  className="inline-flex min-h-[44px] md:min-h-[32px] cursor-pointer items-center gap-2 text-[14px] text-gray-900"
                >
                  <input
                    ref={form.reason === value ? reasonRef : undefined}
                    type="radio"
                    name="billing-pause-reason"
                    value={value}
                    checked={form.reason === value}
                    onChange={() => setForm((f) => ({ ...f, reason: value }))}
                    className="h-4 w-4"
                  />
                  {label}
                </label>
              ))}
            </div>
          </fieldset>

          {form.reason === 'VEHICLE_REPAIR' && (
            <div role="group" aria-labelledby="billing-pause-maintenance">
              <p
                id="billing-pause-maintenance"
                className="text-[13px] text-gray-500 mb-1"
              >
                Maintenance <span className="text-red-500">*</span>
              </p>
              {maintenanceState === 'error' ? (
                <div className="flex flex-wrap items-center gap-3 text-[13px]">
                  <span className="text-red-500">
                    Gagal memuat maintenance.
                  </span>
                  <button
                    type="button"
                    onClick={() => loadMaintenances()}
                    className="inline-flex min-h-[44px] items-center text-primary underline md:min-h-0"
                  >
                    Muat ulang
                  </button>
                </div>
              ) : maintenanceState === 'ok' && maintenances.length === 0 ? (
                <div className="flex flex-col gap-2 rounded-lg border border-dashed border-gray-200 bg-gray-50 p-3 text-[13px] text-gray-700">
                  <span>Belum ada maintenance terbuka untuk {plate}.</span>
                  <span className="flex flex-wrap items-center gap-3">
                    <Button
                      variant="white"
                      size="small"
                      onClick={() => setIsCreateMaintenanceOpen(true)}
                    >
                      <i
                        className="ri-tools-line mr-1 text-[14px]"
                        aria-hidden="true"
                      ></i>
                      Buat maintenance
                    </Button>
                    <button
                      type="button"
                      onClick={() => loadMaintenances()}
                      className="inline-flex min-h-[44px] items-center text-primary underline md:min-h-0"
                    >
                      Muat ulang
                    </button>
                  </span>
                </div>
              ) : (
                <SearchableDropdown
                  options={maintenanceOptions}
                  value={form.maintenanceID}
                  onChange={(value: string) =>
                    setForm((f) => ({ ...f, maintenanceID: value }))
                  }
                  placeholder={
                    maintenanceState === 'loading'
                      ? 'Memuat maintenance...'
                      : 'Pilih maintenance'
                  }
                  loading={maintenanceState === 'loading'}
                />
              )}
            </div>
          )}

          <div>
            <label
              htmlFor="billing-pause-start"
              className="block text-[13px] text-gray-500 mb-1"
            >
              Mulai dijeda <span className="text-red-500">*</span>
            </label>
            <DatePicker
              value={form.startDate}
              minDate={minStartDate}
              maxDate={today}
              onChange={(value) =>
                value &&
                setForm((f) => ({ ...f, startDate: value.slice(0, 10) }))
              }
              inputProps={{ id: 'billing-pause-start' }}
            />
            <FieldHelper>
              {needsMaintenance
                ? 'Paling awal tanggal maintenance dibuka.'
                : `Paling awal ${formatTimelineDate(minStartDate)}${
                    selectedMaintenance &&
                    isoDate(selectedMaintenance.maintenanceDate) ===
                      minStartDate
                      ? ' (tanggal maintenance dibuka)'
                      : ''
                  }.`}{' '}
              Tidak bisa di masa depan.
            </FieldHelper>
          </div>

          <div>
            <label
              htmlFor="billing-pause-notes"
              className="block text-[13px] text-gray-500 mb-1"
            >
              Catatan{' '}
              {form.reason === 'OTHER' ? (
                <span className="text-red-500">*</span>
              ) : (
                '(opsional)'
              )}
            </label>
            <TextareaField
              id="billing-pause-notes"
              value={form.notes}
              onChange={(notes) => setForm((f) => ({ ...f, notes }))}
              maxLength={500}
              rows={3}
            />
          </div>

          <div
            aria-live="polite"
            aria-busy={preview?.status === 'loading'}
            className="rounded-lg bg-gray-50 p-4"
          >
            <h3 className="mb-2 text-[13px] font-semibold text-gray-700">
              Dampak
            </h3>
            {needsMaintenance ? (
              <p className="text-[13px] text-gray-600">
                Pilih maintenance untuk melihat dampak.
              </p>
            ) : preview?.status === 'error' ? (
              <p className="text-[13px]">
                <span className="text-red-500">{preview.error}</span>{' '}
                <button
                  type="button"
                  onClick={() => setRetryToken((n) => n + 1)}
                  className="inline-flex min-h-[44px] items-center text-primary underline md:min-h-0"
                >
                  Coba lagi
                </button>
              </p>
            ) : result ? (
              <>
                <ImpactRow
                  strong
                  label={`${result.cancelledCharges.count} tagihan PENDING dibatalkan`}
                  value={formatRupiah(result.cancelledCharges.amount, 'Rp0', {
                    space: false,
                  })}
                />
                <ImpactRow
                  label="Hari sudah dibayar di rentang ini"
                  value={`${result.paidDaysKept} hari, tetap tercatat`}
                />
                {result.nextFreeDay && (
                  <ImpactRow
                    label={`Hari gratis ${formatTimelineDate(result.nextFreeDay)}`}
                    value="hangus bila masih dijeda"
                  />
                )}
                <ImpactRow
                  label="Eskalasi"
                  value={`dibekukan di ${
                    ESCALATION_STATUS_LABEL[result.escalation.status] ??
                    result.escalation.status
                  } (telat ${result.escalation.daysLate} hari)`}
                />
              </>
            ) : (
              <div className="space-y-3 py-1" data-testid="impact-loading">
                <Skeleton className="h-3.5 w-[90%]" />
                <Skeleton className="h-3.5 w-[70%]" />
                <Skeleton className="h-3.5 w-[80%]" />
                <Skeleton className="h-3.5 w-[60%]" />
              </div>
            )}
          </div>

          <div role="alert">
            <FieldError>{submitError}</FieldError>
          </div>
        </div>
      </SideBarModal>

      <CreateMaintenanceModal
        isOpen={isCreateMaintenanceOpen}
        onClose={() => setIsCreateMaintenanceOpen(false)}
        onSubmit={handleCreateMaintenance}
        clearFormOnClose
        initialFormData={{
          vehicleID: vehicleID ?? '',
          driverID: detail.driver?.id ?? 0,
          driverCode: detail.driver?.code ?? '',
          driverName: detail.driver?.name ?? '',
        }}
        disableVehicleSelection
        initialVehicleLabel={[plate, formatVehicleLabel(detail.vehicle)]
          .filter(Boolean)
          .join(' - ')}
      />
    </>
  );
};

export default BillingPauseModal;
